import React, { useEffect, useState } from 'react'
import {
  Grid,
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  TableBody,
  Button,
  Typography,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Fade,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import * as actions from "./actions"
import { useParams } from "react-router-dom"
import { toastError } from '../../utils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AlbumIcon from '@mui/icons-material/Album';
import PersonIcon from '@mui/icons-material/Person';
import LabelIcon from '@mui/icons-material/Label';
import CategoryIcon from '@mui/icons-material/Category';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import { API_URL } from '../../constants';
import moment from "moment";
import { useHistory } from "react-router-dom";
import SEO from '../../components/SEO';

const PageContainer = styled(Box)({
  minHeight: 'calc(100vh - 64px)',
  background: 'linear-gradient(135deg, #e8eef3 0%, #dce4ec 100%)',
  padding: '32px 24px',
  position: 'relative',
});

const StatusBanner = styled(Box)(({ statustype }) => {
  const getStatusColor = () => {
    switch (statustype) {
      case 'approved':
        return { bg: '#4caf50', color: '#ffffff' };
      case 'in_review':
        return { bg: '#ff9800', color: '#ffffff' };
      case 'rejected':
        return { bg: '#f44336', color: '#ffffff' };
      case 'takendown':
        return { bg: '#e91e63', color: '#ffffff' };
      default:
        return { bg: '#9e9e9e', color: '#ffffff' };
    }
  };

  const colors = getStatusColor();

  return {
    background: colors.bg,
    color: colors.color,
    padding: '16px 24px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '1.125rem',
    fontWeight: 600,
    marginBottom: '24px',
    boxShadow: `0 4px 12px ${colors.bg}40`,
  };
});

const InfoCard = styled(Card)({
  borderRadius: '16px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
  background: 'white',
  marginBottom: '24px',
});

const InfoRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  padding: '12px 0',
  borderBottom: '1px solid #f0f0f0',
  '&:last-child': {
    borderBottom: 'none',
  },
});

const InfoLabel = styled(Typography)({
  fontWeight: 600,
  color: '#5f6368',
  fontSize: '0.875rem',
  minWidth: '160px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const InfoValue = styled(Typography)({
  color: '#202124',
  fontSize: '0.9375rem',
  fontWeight: 500,
  flex: 1,
});

const BackButton = styled(Button)({
  fontFamily: "'DM Sans', sans-serif",
  background: 'white',
  color: '#202124',
  borderRadius: '12px',
  padding: '10px 20px',
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(0, 0, 0, 0.12)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'white',
    borderColor: '#1a73e8',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(26, 115, 232, 0.2)',
  },
});

const EditButton = styled(Button)({
  fontFamily: "'DM Sans', sans-serif",
  background: 'linear-gradient(135deg, #f44336 0%, #e91e63 100%)',
  color: 'white',
  borderRadius: '12px',
  padding: '12px 24px',
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #d32f2f 0%, #c2185b 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(244, 67, 54, 0.4)',
  },
});

const AlbumArtwork = styled('img')({
  width: '100%',
  height: 'auto',
  aspectRatio: '1',
  objectFit: 'cover',
  display: 'block',
});

const StyledTableContainer = styled(TableContainer)({
  borderRadius: '16px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
  backgroundColor: 'white',
  '& .MuiTable-root': {
    fontFamily: "'DM Sans', sans-serif",
    backgroundColor: 'white',
  },
  '& .MuiTableHead-root': {
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    '& .MuiTableCell-head': {
      color: 'white',
      fontWeight: 700,
      fontSize: '0.9375rem',
      fontFamily: "'DM Sans', sans-serif",
    },
  },
  '& .MuiTableBody-root': {
    backgroundColor: 'white',
    '& .MuiTableRow-root': {
      backgroundColor: 'white !important',
      transition: 'background-color 0.2s ease',
      '&:nth-of-type(odd)': {
        backgroundColor: '#f9f9f9 !important',
      },
      '&:hover': {
        backgroundColor: '#f0f7ff !important',
      },
    },
  },
  '& .MuiTableCell-body': {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.875rem',
    color: '#202124',
    borderBottom: '1px solid #f0f0f0',
  },
});

const TrackStatusChip = styled(Chip)(({ trackstatus }) => {
  const getStatusColor = () => {
    switch (trackstatus) {
      case 'approved':
        return { bg: '#4caf50', color: '#ffffff' };
      case 'in-review':
        return { bg: '#ff9800', color: '#ffffff' };
      case 'rejected':
        return { bg: '#f44336', color: '#ffffff' };
      case 'takendown':
        return { bg: '#e91e63', color: '#ffffff' };
      case 'on-hold':
        return { bg: '#9e9e9e', color: '#ffffff' };
      default:
        return { bg: '#2196f3', color: '#ffffff' };
    }
  };

  const colors = getStatusColor();

  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontWeight: 600,
    fontSize: '0.75rem',
    height: '24px',
    fontFamily: "'DM Sans', sans-serif",
  };
});

function ViewRelease(props) {

    const params = useParams();
    const history = useHistory();
    const [catalog, setCatalog] = useState({})

    useEffect(() => {
        if (params.id) {
            props.setLoader(true)
            actions.getCatelogById(params.id).then(res => {
                props.setLoader(false)
                setCatalog(res.body)
            }).catch(err => {
                props.setLoader(false)
                toastError(err.message)
            })
        }
    }, [])

    const {
        album_artwork = "",
        audios = [],
        status = 0,

        lable_name = "",
        primary_artist1 = "",
        release_title = "",
        genre = "",

        release_id = '',
        reason = '',
        number_of_tracks = 0,
        release_date = "",
        crbt = "",
        video_platforms = "",

    } = catalog || {}

    const getStatusDetails = () => {
      switch (status) {
        case 'approved':
          return { label: 'Approved', icon: <CheckCircleIcon /> };
        case 'in_review':
          return { label: 'In Review', icon: <AccessTimeIcon /> };
        case 'rejected':
          return { label: 'Rejected', icon: <CancelIcon /> };
        case 'takendown':
          return { label: 'Taken Down', icon: <RemoveCircleOutlineIcon /> };
        default:
          return { label: status, icon: null };
      }
    };

    const statusDetails = getStatusDetails();

    return (
        <PageContainer>
          <SEO
            title={release_title ? `${release_title} - Release Details` : 'Release Details'}
            description={`View detailed information about ${release_title || 'this release'} by ${primary_artist1 || 'artist'} including tracks, status, and distribution details.`}
          />
          <Fade in timeout={800}>
            <Box>
              {/* Header with Back Button */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <BackButton
                  startIcon={<ArrowBackIcon />}
                  onClick={() => history.push('/catalog')}
                >
                  Back to Catalog
                </BackButton>

                {status === 'rejected' && (
                  <EditButton
                    startIcon={<EditIcon />}
                    onClick={() => history.push(`/create_releases/${release_id}`)}
                  >
                    Edit & Re-submit
                  </EditButton>
                )}
              </Box>

              {/* Main Release Info */}
              <Grid container spacing={3}>
                {/* Album Artwork & Status */}
                <Grid item xs={12} md={3}>
                  {/* Album Artwork */}
                  <InfoCard sx={{ mb: 2 }}>
                    <AlbumArtwork
                      src={`${API_URL}/${album_artwork}`}
                      alt={release_title}
                      onError={(e) => {
                        // Remove error handler to prevent infinite loop
                        e.target.onerror = null;
                        // Use a data URI as fallback (no server request needed)
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2UwZTBlMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
                      }}
                    />
                  </InfoCard>

                  {/* Status Banner */}
                  {status && (
                    <StatusBanner statustype={status}>
                      {statusDetails.icon}
                      <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>
                        Status: {statusDetails.label}
                      </Typography>
                    </StatusBanner>
                  )}
                </Grid>

                {/* Release Details */}
                <Grid item xs={12} md={9}>
                  {/* Rejection Reason */}
                  {status === 'rejected' && reason && (
                    <InfoCard sx={{ mb: 2, borderLeft: '4px solid #f44336' }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ color: '#f44336', fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CancelIcon /> Rejection Reason
                        </Typography>
                        <Typography sx={{ color: '#202124', fontSize: '0.9375rem', lineHeight: 1.6 }}>
                          {reason}
                        </Typography>
                      </CardContent>
                    </InfoCard>
                  )}
                  <InfoCard>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#202124' }}>
                        Release Information
                      </Typography>

                      <InfoRow>
                        <InfoLabel>
                          <AlbumIcon sx={{ fontSize: 18 }} />
                          Release Title
                        </InfoLabel>
                        <InfoValue>{release_title}</InfoValue>
                      </InfoRow>

                      <InfoRow>
                        <InfoLabel>
                          <PersonIcon sx={{ fontSize: 18 }} />
                          Primary Artist
                        </InfoLabel>
                        <InfoValue>{primary_artist1}</InfoValue>
                      </InfoRow>

                      <InfoRow>
                        <InfoLabel>
                          <LabelIcon sx={{ fontSize: 18 }} />
                          Label
                        </InfoLabel>
                        <InfoValue>{lable_name}</InfoValue>
                      </InfoRow>

                      <InfoRow>
                        <InfoLabel>
                          <CategoryIcon sx={{ fontSize: 18 }} />
                          Genre
                        </InfoLabel>
                        <InfoValue>{genre}</InfoValue>
                      </InfoRow>

                      <InfoRow>
                        <InfoLabel>
                          <MusicNoteIcon sx={{ fontSize: 18 }} />
                          Number of Tracks
                        </InfoLabel>
                        <InfoValue>{number_of_tracks}</InfoValue>
                      </InfoRow>

                      <InfoRow>
                        <InfoLabel>
                          <CalendarTodayIcon sx={{ fontSize: 18 }} />
                          Release Date
                        </InfoLabel>
                        <InfoValue>{moment(Number(release_date)).format('DD/MM/YYYY')}</InfoValue>
                      </InfoRow>

                      <InfoRow>
                        <InfoLabel>Explicit Version</InfoLabel>
                        <InfoValue>{Array.isArray(audios) && audios.length ? audios[0].explicit_version : '-'}</InfoValue>
                      </InfoRow>

                      <InfoRow>
                        <InfoLabel>Price Tier</InfoLabel>
                        <InfoValue>{Array.isArray(audios) && audios.length ? audios[0].price_tier : '-'}</InfoValue>
                      </InfoRow>
                    </CardContent>
                  </InfoCard>
                </Grid>
              </Grid>

              {/* Tracks Section */}
              <Box sx={{ mt: 4, mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#202124', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MusicNoteIcon sx={{ color: '#1a73e8' }} />
                  Tracks ({audios?.length || 0})
                </Typography>
              </Box>

              <StyledTableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>No.</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Artist</TableCell>
                      <TableCell>Genre</TableCell>
                      <TableCell>Product Number</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {
                      audios && audios.length ? audios.map((item, key) => {
                        let trackStatus = 'in-review';
                        let trackStatusLabel = 'In-Review';

                        if (item.status !== 2 && status === 'rejected') {
                          trackStatus = 'on-hold';
                          trackStatusLabel = 'On-hold';
                        } else if (item.status === 2) {
                          trackStatus = 'rejected';
                          trackStatusLabel = 'Rejected';
                        } else if (item.status === 1) {
                          trackStatus = 'approved';
                          trackStatusLabel = 'Approved';
                        } else if (item.status === 3) {
                          trackStatus = 'takendown';
                          trackStatusLabel = 'Takendown';
                        }

                        return (
                          <TableRow key={key}>
                            <TableCell>{key + 1}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{item.title}</TableCell>
                            <TableCell>{item.primary_artist1}</TableCell>
                            <TableCell>{item.genre}</TableCell>
                            <TableCell>{`FM-AU-21${item.audio_id}`}</TableCell>
                            <TableCell>
                              <TrackStatusChip
                                trackstatus={trackStatus}
                                label={trackStatusLabel}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      }) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography sx={{ color: '#5f6368', fontStyle: 'italic', py: 2 }}>
                              No tracks found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </TableBody>
                </Table>
              </StyledTableContainer>
            </Box>
          </Fade>
        </PageContainer>
    )
}

export default ViewRelease
